/**
 * Bahadura Permanent RL / Temporary RL shared constants.
 * Mirrors civic-plan-backend/api/config/phaseConstants.js exactly.
 * See plan: C:\Users\HP\.claude\plans\soft-zooming-candy.md
 *
 * rl_application_type decides which journey an application takes:
 *  - PERMANENT: runs the plain original flow unmodified, no phase branching at all.
 *  - TEMPORARY: runs the original flow as Phase 1, then optionally the Phase 2
 *    continuation flow (current_phase / permanent_rl_status apply only here).
 */

export const RL_APPLICATION_TYPE = {
  PERMANENT: "Permanent",
  TEMPORARY: "Temporary",
} as const;

export const PHASE = {
  TEMP_RL: "Phase_1",
  PERMANENT_RL: "Phase_2",
} as const;

export const PERMANENT_RL_STATUS = {
  DRAFT: "Draft",
  PENDING_L1: "Pending Level 1 Review",
  PENDING_L2: "Pending Level 2 Review",
  PENDING_L3: "Pending Level 3 Review",
  APPROVED: "Approved",
  RETURNED_L1: "Returned By Level 1 Reviewer",
  RETURNED_L2: "Returned By Level 2 Reviewer",
  RETURNED_L3: "Returned By Level 3 Reviewer",
  REJECTED_L2: "Rejected By Level 2 Reviewer",
  REJECTED_L3: "Rejected By Level 3 Reviewer",
} as const;

export const DOCUMENT_CONFIG_ID = {
  PERMANENT_RL_K_PRAT: 9991,
  PERMANENT_RL_REGISTRY_CERT: 9992,
} as const;

/**
 * The "Application Type" field (uid rl_application_type) is a DB-driven
 * `configurable-select` field (cp_form_fields / cp_field_options), same as
 * road_leading_to_land. Its raw react-hook-form value is therefore the
 * numeric cp_field_options.id of the chosen option (see RenderSelect's
 * `valueAsNumber: true`), NOT the "Permanent"/"Temporary" string — that
 * string conversion only happens server-side in transformApplication().
 * These option IDs are inserted explicitly in the DB migration so both
 * sides can agree on fixed values, same convention as the 9991/9992
 * document_config_id's above.
 */
export const RL_APPLICATION_TYPE_OPTION_ID = {
  PERMANENT: 9993,
  TEMPORARY: 9994,
} as const;

/**
 * True only for a Temporary-type application currently in its Phase 2
 * (post-completion Permanent RL) continuation. A Permanent-type application
 * never satisfies this, regardless of current_phase.
 */
export function isPhase2Continuation(application: any): boolean {
  return (
    application?.rl_application_type === RL_APPLICATION_TYPE.TEMPORARY &&
    application?.current_phase === PHASE.PERMANENT_RL
  );
}

/**
 * "Permanent"/"Temporary" display label for the navbar Application Type field.
 * See plan: C:\Users\HP\.claude\plans\soft-zooming-candy.md
 */
export function getApplicationTypeLabel(rlApplicationType?: unknown): string {
  if (rlApplicationType === RL_APPLICATION_TYPE.PERMANENT) return "Permanent";
  if (rlApplicationType === RL_APPLICATION_TYPE.TEMPORARY) return "Temporary";
  return "Nil";
}

/**
 * "Phase I"/"Phase II" display label for the navbar Application Phase field.
 */
export function getApplicationPhaseLabel(currentPhase?: unknown): string {
  if (currentPhase === PHASE.PERMANENT_RL) return "Phase II";
  if (currentPhase === PHASE.TEMP_RL) return "Phase I";
  return "Nil";
}

/**
 * Combined "Permanent" / "Temporary (Phase I)" / "Temporary (Phase II)"
 * display label for the Application Type column on application-listing
 * tables. See plan: C:\Users\HP\.claude\plans\soft-zooming-candy.md
 */
export function getApplicationTypeColumnLabel(
  application:
    | { rl_application_type?: unknown; current_phase?: unknown }
    | null
    | undefined
): string {
  if (!application) return "NA";
  if (application.rl_application_type === RL_APPLICATION_TYPE.PERMANENT) {
    return "Permanent";
  }
  if (application.rl_application_type === RL_APPLICATION_TYPE.TEMPORARY) {
    return application.current_phase === PHASE.PERMANENT_RL
      ? "Temporary"
      : "Temporary";
  }
  return "NA";
}

/**
 * Plot-detail / land-possession / application-type fields that must always
 * render on the L1/L2/L3 review screens' <ApplicationList /> regardless of
 * whether `selectedFields` resolved (it can come up empty if service-id to
 * service-name resolution fails), matching the existing force-include
 * precedent already used there for typeOfApplicationSubmitterBy/submittedBy.
 */
export const PLOT_AND_LAYOUT_FIELD_UIDS = [
  "rl_application_type",
  "unauthorized_layout_name",
  "area_as_per_7_12",
  "area_as_per_revenue_record",
  "area_as_per_sale_deed",
  "area_under_possession",
  "road_leading_to_land",
  "plot_usage_dp",
  "address",
] as const;
